import React, { useContext } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import PropTypes from "prop-types";
import { UserContext } from "../context/userContext";
import { SidebarData } from "../core/json/siderbar_data";
import Header from "../InitialPage/Sidebar/Header";
import Sidebar from "../InitialPage/Sidebar/Sidebar";
import { publicRoutes, posRoutes, pagesRoute } from "./router.link";
import { Outlet } from "react-router-dom";
import { useSelector } from "react-redux";
import Loader from "../feature-module/loader/loader";
import EmployeesGrid from "../feature-module/hrm/employeesgrid";
import CategoryList from "../feature-module/inventory/categorylist";
import EmployeeDashboard from "../feature-module/dashboard/employee-dashboard";

const AllRoutes = () => {
    const data = useSelector((state) => state.toggle_header);
    const { user, loading } = useContext(UserContext); // Updated to include loading

    const hasModuleAccess = (moduleLabel) => {
        if (moduleLabel === "Settings") return true;
        if (!user || !user.permissions) return false;
        return user.permissions.some(
            (perm) =>
                perm.label === moduleLabel ||
                (perm.moduleId && perm.moduleId.label === moduleLabel)
        );
    };

    const hasFunctionAccess = (moduleLabel, functionLabel) => {
        if (moduleLabel === "Settings") return true;
        if (!user || !user.permissions) return false;
        const module = user.permissions.find(
            (perm) =>
                perm.label === moduleLabel ||
                (perm.moduleId && perm.moduleId.label === moduleLabel)
        );
        if (!module) return false;
        return module.functions?.some(
            (func) =>
                func.label === functionLabel ||
                (func.functionId && func.functionId.label === functionLabel)
        );
    };

    const hasSubFunctionAccess = (moduleLabel, functionLabel, subFunctionLabel) => {
        if (moduleLabel === "Settings") return true;
        if (!user || !user.permissions) return false;
        const module = user.permissions.find(
            (perm) =>
                perm.label === moduleLabel ||
                (perm.moduleId && perm.moduleId.label === moduleLabel)
        );
        if (!module) return false;
        const func = module.functions?.find(
            (f) =>
                f.label === functionLabel ||
                (f.functionId && f.functionId.label === functionLabel)
        );
        if (!func) return false;
        return func.subFunctions?.some(
            (sub) =>
                sub.label === subFunctionLabel ||
                (sub.functionId && sub.functionId.label === subFunctionLabel)
        );
    };

    const routePermissionMap = React.useMemo(() => {
        const map = {};
        SidebarData.forEach((module) => {
            module.submenuItems?.forEach((menuItem) => {
                if (menuItem.link) {
                    map[menuItem.link] = {
                        module: module.label,
                        function: menuItem.label,
                    };
                }
                menuItem.submenuItems?.forEach((subItem) => {
                    if (subItem.link) {
                        map[subItem.link] = {
                            module: module.label,
                            function: menuItem.label,
                            subFunction: subItem.label,
                        };
                    } 
                });
            });
        });
        return map;
    }, []);

    const hasRouteAccess = (path) => {
        if (!user || !user.permissions) {
            console.log(`No user or permissions for path: ${path}`);
            return false;
        }

        // ── Special permission-controlled routes (bypass Settings shortcut) ───
        // DB: Module "Settings" → Function "Audit Logs" (direct function, not sub-function)
        if (path === "/audit-logs") {
            for (const perm of user.permissions) {
                const modLabel = (perm.label || perm.moduleId?.label || "").trim().toLowerCase();
                if (modLabel !== "settings") continue;
                const functions = perm.functions || [];
                for (const func of functions) {
                    const funcLabel = (func.label || func.functionId?.label || "").trim().toLowerCase();
                    if (funcLabel === "audit logs" && func.permissions?.view === true) return true;
                }
            }
            return false;
        }

        const routePerm = routePermissionMap[path];
        if (!routePerm) {
            console.log(`No permission mapping for path: ${path}, allowing access`);
            return true; 
        }
        const { module, function: func, subFunction } = routePerm;
        let access;
        if (subFunction) {
            access = hasSubFunctionAccess(module, func, subFunction);
        } else if (func) {
            access = hasFunctionAccess(module, func);
        } else {
            access = hasModuleAccess(module);
        }
        console.log(`Access for ${path}: ${access}`);
        return access;
    };

    const ProtectedRoute = ({ element, path }) => {
        if (loading) {
            console.log(`User data is loading for path: ${path}`);
            return <Loader />; // Show loader while user data is loading
        }

        const isPublicRoute = pagesRoute.some((route) => route.path === path);
        if (isPublicRoute) {
            console.log(`Public route accessed: ${path}`);
            return element;
        }

        if (hasRouteAccess(path)) {
            return element;
        }
        
        if (path !== "/employee-dashboard" && !hasRouteAccess("/employee-dashboard")) {
            console.log(`Unauthorized for ${path}, no access to /employee-dashboard, redirecting to /signin`);
            return <Navigate to="/signin" replace />;
        }

        console.log(`Unauthorized for ${path}, redirecting to /employee-dashboard`);
        return <Navigate to="/employee-dashboard" replace />;
    };

    ProtectedRoute.propTypes = {
        element: PropTypes.element.isRequired,
        path: PropTypes.string.isRequired,
    };

    const HeaderLayout = () => (
        <div className={`main-wrapper ${data ? "header-collapse" : ""}`}>
            <Header />
            <Sidebar />
            <Outlet />
            <Loader />
        </div>
    );

    const Authpages = () => (
        <div className={data ? "header-collapse" : ""}>
            <Outlet />
            <Loader />
        </div>
    );

    const Pospages = () => (
        <div>
            <Header />
            <Outlet />
            <Loader />
        </div>
    );

    return (
        <div>
            <Routes>
                <Route path="/pos" element={<Pospages />}>
                    {posRoutes.map((route, id) => (
                        <Route
                            key={id}
                            path={route.path}
                            element={<ProtectedRoute element={route.element} path={route.path} />}
                        />
                    ))}
                </Route>

                <Route path="/" element={<HeaderLayout />}>
                    {publicRoutes.map((route, id) => (
                        <Route
                            key={id}
                            path={route.path}
                            element={<ProtectedRoute element={route.element} path={route.path} />}
                        />
                    ))}
                    <Route
                        path="/employee-dashboard"
                        element={<ProtectedRoute element={<EmployeeDashboard />} path="/employee-dashboard" />}
                    />
                </Route>

                <Route path="/" element={<Authpages />}>
                    {pagesRoute.map((route, id) => (
                        <Route
                            key={id}
                            path={route.path}
                            element={<ProtectedRoute element={route.element} path={route.path} />}
                        />
                    ))}
                    <Route
                        path="/employee-dashboard"
                        element={<ProtectedRoute element={<EmployeeDashboard />} path="/employee-dashboard" />}
                    />
                </Route>

                <Route
                    path="/employees-grid"
                    element={<ProtectedRoute element={<EmployeesGrid />} path="/employees-grid" />}
                />
                <Route
                    path="/category-list"
                    element={<ProtectedRoute element={<CategoryList />} path="/category-list" />}
                />
            </Routes>
        </div>
    );
};

export default AllRoutes;